import React from 'react';
import { Card } from '@wds/card';
import { Badge } from '@wds/badge';
import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { Grid } from '@wds/grid';
import { Typography } from '@wds/typography';

import PropTypes from 'prop-types';

import ApplicationCardMenu from './application_card_menu';

const CardTitle = (props) => {
	const {
		application,
	} = props;
	return (
		<Grid noBleeds>
			{application.decision.decision_warning && (
				<span className="application-badge-wrapper"><Badge color="upgrade">{application.decision.decision_warning}</Badge></span>
			)}
		</Grid>
	);
};

const SubmittedFragment = () => (
	<div className="d-flex justify-content-center sma-application-card-submitted mt-auto mb-auto">
		<div className="align-self-center">
			<div className="sma-submitted">
				<i className="nc-icon-mini ui-1_check-circle-08" aria-hidden="true" />
			</div>
		</div>
		<div className="text-left pl-2 sma-submitted-text">
			<h6 className="mb-0">{gettext('Submitted')}</h6>
		</div>
	</div>
);

const AwardedFragment = (props) => {
	const {
		application,
	} = props;
	const awardedValue = application.decision._awarded_value;
	return (
		<div className="d-flex justify-content-center sma-application-card-awarded mt-auto mb-auto">
			<div className="align-self-center">
				<div className="sma-awarded">
					<i className="nc-icon-mini sport_trophy" aria-hidden="true" />
				</div>
			</div>
			<div className="text-left pl-2 sma-awarded-text">
				<h6 className="mb-0">{gettext('Awarded')}</h6>
				<div className="sma-shorten-to-one-line"><p className="mb-0">{awardedValue}</p></div>
			</div>
		</div>
	);
};

const DeclinedFragment = () => (
	<div className="d-flex justify-content-center sma-application-card-declined mt-auto mb-auto">
		<div className="align-self-center">
			<div className="sma-declined">
				<i className="nc-icon-mini ui-1_circle-remove" aria-hidden="true" />
			</div>
		</div>
		<div className="text-left pl-2 sma-declined-text">
			<h6 className="mb-0">{gettext('Declined')}</h6>
		</div>
	</div>
);

const ApplicantDeclinedFragment = () => (
	<div className="d-flex justify-content-center sma-application-card-declined mt-auto mb-auto">
		<div className="align-self-center">
			<div className="sma-declined">
				<i className="nc-icon-mini ui-1_circle-remove" aria-hidden="true" />
			</div>
		</div>
		<div className="text-left pl-2 sma-declined-text">
			<h6 className="mb-0">{gettext('You have declined this award')}</h6>
		</div>
	</div>
);

const ProgressFragment = (props) => {
	const {
		application,
	} = props;
	const {
		progress,
	} = application;
	return (
		<div className="application-card-progress">
			<div className="text-muted text-center wds-m-b-1">{progress.msg}</div>
			<div className="progress sma-application-card-progress">
				<div
					className="progress-bar"
					role="progressbar"
					aria-valuenow={progress.pct}
					aria-valuemin="0"
					aria-valuemax="100"
					style={{ width: `${progress.pct}%` }}
					aria-label={`Application progress: ${progress.pct} percent`}
				/>
			</div>
		</div>
	);
};

export default class ApplicationList extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			applications: props.applications,
		};
	}

	render() {
		const {
			applications,
		} = this.state;

		return (
			<Grid className="wds-p-1 application-list" noBleeds>
				{ applications.map((application) => {
					const menuOptions = [
						{
							action: 'download',
							label: gettext('Download'),
						}, {
							action: 'preview',
							label: gettext('Preview'),
						},
					];
					if (application.can_delete) {
						menuOptions.push({
							action: 'delete',
							label: gettext('Delete'),
						});
					}

					let primaryButtonText;
					let primaryButtonColor = 'primary';
					let primaryButtonAriaLabel;
					if (application.progress.show_start === true) {
						primaryButtonText = gettext('Start');
						primaryButtonAriaLabel = interpolate(
							gettext('Start application for %(programName)s'),
							{ programName: application.award.name },
							true
						);
					} else if (application.progress.show_continue === true) {
						primaryButtonText = gettext('Continue');
						primaryButtonAriaLabel = interpolate(
							gettext('Continue application for %(programName)s'),
							{ programName: application.award.name },
							true
						);
					} else {
						primaryButtonText = gettext('View');
						primaryButtonColor = 'muted';
						primaryButtonAriaLabel = interpolate(
							gettext('View application for %(programName)s'),
							{ programName: application.award.name },
							true
						);
					}
					const membersText = interpolate(
						ngettext('%s member', '%s members', application.collaborator_count),
						[application.collaborator_count],
					);
					return (
						<Grid.Item lg={4} md={6} sm={12} xs={12} className="ssss" key={application.id}>
							<Box mb={3} className='wds-card application-card'>
								<Box element='header' className='wds-card__header'>
									<Box
										display="flex"
										alignItems="center"
										justifyContent="space-between"
									>
										<CardTitle application={application} />
										<div className="wds-card__addon">
											<ApplicationCardMenu application={application} items={menuOptions} />
										</div>
									</Box>
								</Box>
								<Box className='wds-card__body'>
									<Typography component="a" href={application.award.listing_url}><h2 className="text-muted sma-automation-application-program-name">{application.award.name}</h2></Typography>
									<Typography>
										<Box className="application-card-title sma-shorten-to-one-line">
											<Typography>
												{application.is_locked && (
													<Typography component="span">
														<i className="nc-icon-mini ui-1_lock" aria-hidden="true" />
														<span className="sr-only">{gettext('Application is locked')}</span>
													</Typography>
												)}
												<h3><b>{application.title}</b></h3>
												{application.collaborator_count > 0 && (
													<i className="nc-icon-mini users_multiple-19 ml-2" data-toggle="tooltip" title={membersText} />
												)}
											</Typography>
										</Box>
										<Box className="application-id">
											<Typography>
												{application.reference_id && application.reference_id !== application.title && (
													application.reference_id
												)}
											</Typography>
										</Box>
									</Typography>
									{application.organization_info.name && (
										<Box className="application-card-title">
											<Typography>
												<small className="text-muted d-block sma-shorten-to-one-line">
													<i className="nc-icon-mini business_building" />
													<Box ml={2}>
														<Typography component="span">{application.organization_info.name}</Typography>
													</Box>
												</small>
											</Typography>
										</Box>
									)}
									<Box className="application-card-deadline sma-shorten-to-one-line">
										<Typography>
											{application.deadline && (
												<Typography>
													<Typography component="span" weight="medium">{gettext('Deadline: ')}</Typography>
													{application.deadline}
												</Typography>
											)}
										</Typography>
									</Box>
									<hr className="wds-m-t-3 wds-m-b-1" />
									<div className="application-card-info wds-m-b-2">
										<div className="application-card-info-top">
											{application.status && (
												<div>
													<Box className="sma-shorten-to-one-line">
														<Typography>
															<Typography component="span">
																<span className="status-label">
																	{gettext('Status: ')}
																</span>
															</Typography>
															<Typography component="span">{application.status}</Typography>
														</Typography>
													</Box>
													<hr className="wds-m-t-1 wds-m-b-3" />
												</div>
											)}
										</div>
										<div className="application-card-info-middle d-flex flex-column justify-content-center">
											{application.decision.awarded && !application.decision.applicant_declined && (
												<AwardedFragment application={application} />
											)}
											{application.decision.declined && !application.decision.applicant_declined && (
												<DeclinedFragment />
											)}
											{application.decision.applicant_declined && (
												<ApplicantDeclinedFragment />
											)}
											{application.is_submitted && !application.decision.decision_date && (
												<SubmittedFragment />
											)}
										</div>
										<div className="application-card-info-bottom d-flex flex-column justify-content-end">
											{application.progress.show_progress && (
												<ProgressFragment application={application} />
											)}
										</div>
									</div>
									<div className="application-button">
										<Button
											aria-label={primaryButtonAriaLabel}
											stretched
											color={primaryButtonColor}
											href={application.url}
										>
											{primaryButtonText}
										</Button>
									</div>
									<div className="wds-flex wds-m-t-3 wds-flex--x-center text-muted">
										<div className="shorten-application-date">
											{application.submitted_date && (
												interpolate(gettext('Submitted on: %(submittedDate)s'), {
													submittedDate: application.submitted_date,
												}, true)
											)}
											{!application.submitted_date && (
												interpolate(gettext('Last edited: %(editDate)s'), {
													editDate: application.last_edited,
												}, true)
											)}
										</div>
									</div>
								</Box>
							</Box>
						</Grid.Item>
					);
				})}
			</Grid>
		);
	}
}

ApplicationList.propTypes = {
	applications: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.number.isRequired,
		title: PropTypes.string.isRequired,
		can_delete: PropTypes.bool.isRequired,
		is_submitted: PropTypes.bool.isRequired,
		submitted_date: PropTypes.string,
		last_edited: PropTypes.string,
	})).isRequired,
};

CardTitle.propTypes = {
	application: PropTypes.shape({
		id: PropTypes.number.isRequired,
	}).isRequired,
};

AwardedFragment.propTypes = {
	application: PropTypes.shape({
		id: PropTypes.number.isRequired,
	}).isRequired,
};

DeclinedFragment.propTypes = {
};

ApplicantDeclinedFragment.propTypes = {
};

ProgressFragment.propTypes = {
	application: PropTypes.shape({
		id: PropTypes.number.isRequired,
	}).isRequired,
};
